import { APP_ENVS } from '@/shared/constants/envs';
import { APP_ROUTES } from '@/shared/constants/navigation';

const LANDING_CDN_BASE_URL = 'https://media.apply.so/static';
const LANDING_FEATURES_BASE_PATH = '/images/landing/features';
const LANDING_AI_AGENTS_BASE_PATH = '/images/landing/ai-agents';

export const LANDING_IMAGES = {
	startups: {
		lossfunk: `${LANDING_CDN_BASE_URL}/lossfunk.png`,
		supermemory: `${LANDING_CDN_BASE_URL}/supermemory.png`,
		sundial: `${LANDING_CDN_BASE_URL}/sundial.png`,
		smallcase: `${LANDING_CDN_BASE_URL}/smallcase.png`,
		leap: `${LANDING_CDN_BASE_URL}/leap.png`,
	},
} as const;

type FeatureStory = {
	/** Stable key, so a page can feature one story elsewhere and skip it in the list. */
	id: string;
	heading: string;
	description: string;
	media: { type: 'video'; src: string; poster: string } | { type: 'image'; src: string };
};

export const FEATURE_STORIES: FeatureStory[] = [
	{
		id: 'hiring-forms',
		heading: 'Create hiring forms',
		description: 'Not the boring ones. Add video questions, widgets and more.',
		media: {
			type: 'video',
			src: `${LANDING_FEATURES_BASE_PATH}/hiring-forms.mp4`,
			poster: `${LANDING_FEATURES_BASE_PATH}/hiring-forms-poster.jpg`,
		},
	},
	{
		id: 'attract-candidates',
		heading: 'Attract candidates',
		description: 'Push to job boards, create a careers page or source via Chrome extension.',
		media: { type: 'image', src: `${LANDING_FEATURES_BASE_PATH}/attract-candidates.png` },
	},
	{
		id: 'screen-candidates',
		heading: 'Screen candidates faster',
		description: 'Let AI read each profile and find the ones that you need.',
		media: { type: 'image', src: `${LANDING_FEATURES_BASE_PATH}/screen-candidates.png` },
	},
	{
		id: 'hiring-funnel',
		heading: 'Track your hiring funnel',
		description: 'See a clear view of where your candidates are in the funnel.',
		media: { type: 'image', src: `${LANDING_FEATURES_BASE_PATH}/hiring-funnel.png` },
	},
	{
		id: 'interview-scheduling',
		heading: 'Schedule interviews',
		description:
			'See calendars of all interviewers, send booking links and easily collect interview feedback.',
		media: { type: 'image', src: `${LANDING_FEATURES_BASE_PATH}/interview-scheduling.png` },
	},
	{
		id: 'more-features',
		heading: 'and a lot more',
		description:
			'Analytics, referrals, collaboration tools and all the essentials your team needs.',
		media: { type: 'image', src: `${LANDING_FEATURES_BASE_PATH}/more-features.png` },
	},
];

/** The funnel story, used as the /free-ats hero image. */
export const HIRING_FUNNEL_STORY = FEATURE_STORIES.find((story) => story.id === 'hiring-funnel');

export const AI_AGENT_SLIDES = [
	{ src: `${LANDING_AI_AGENTS_BASE_PATH}/claude.png`, alt: 'Using Binary with Claude' },
	{ src: `${LANDING_AI_AGENTS_BASE_PATH}/chatgpt.png`, alt: 'Using Binary with ChatGPT' },
	{ src: `${LANDING_AI_AGENTS_BASE_PATH}/slack.png`, alt: 'Using Binary in Slack' },
] as const;

export type FaqEntry = {
	question: string;
	answer: string;
	link?: { href: string; match: string; accent?: boolean };
	/** Rendered open as plain text, kept last, and left out of FAQ structured data. */
	isStatic?: boolean;
};

export const FAQ_DATA: FaqEntry[] = [
	{
		question: 'Is it really free? How?',
		answer: `Yes. Our free plan supports unlimited jobs, hiring forms and candidates. No catch. Because a lot of customers love our product, we thought the best way to market it is just have a generous free plan. We don't have any sales team and that helps us keep our costs low too.`,
		link: { href: APP_ROUTES.FREE_ATS, match: 'free plan' },
	},
	{
		question: 'How is it different from other ATS?',
		answer: `When we started ${APP_ENVS.COMPANY_NAME}, we wanted to build a very different kind of ATS. One that is lightweight, fast and well-designed. One that is built for the AI era. One that is so simple that it doesn't need a tutorial. One that you can start using immediately without speaking to sales. We can go on and on but you get the idea.`,
	},
	{
		question: 'What if I already have an ATS?',
		answer: `If you are stuck with an ATS you don't love, we know how that feels. Pick any annual plan and the months left on your current subscription are free on Binary, and we help you migrate all your existing data within 48 hours. Reach out at ${APP_ENVS.COMPANY_SUPPORT_EMAIL}.`,
		link: { href: APP_ROUTES.SWITCH, match: 'migrate all your existing data' },
	},
	{
		question: 'Can I add team members?',
		answer: `Yes. ${APP_ENVS.COMPANY_NAME} lets you create workspaces, add users and decide their roles and permissions. All our plans support unlimited team members.`,
	},

	{
		question: 'Can I migrate existing data?',
		answer: `Absolutely. There is an import feature as soon as you sign-up or you can also ask AI to connect to our MCP and do this.`,
	},
	{
		question: 'Have a different question?',
		answer: 'Ask our AI: binary.so/ai',
		link: { href: APP_ROUTES.AI_AGENT, match: 'binary.so/ai', accent: true },
		isStatic: true,
	},
];

export const TRUSTED_STARTUPS_LOGOS = [
	{
		src: LANDING_IMAGES.startups.sundial,
		width: 644,
		height: 256,
		alt: 'Sundial',
	},
	{
		src: LANDING_IMAGES.startups.supermemory,
		width: 1984,
		height: 512,
		alt: 'Supermemory',
	},
	{
		src: LANDING_IMAGES.startups.lossfunk,
		width: 508,
		height: 256,
		alt: 'Lossfunk',
	},
	{
		src: LANDING_IMAGES.startups.smallcase,
		width: 668,
		height: 256,
		alt: 'smallcase',
	},
	{
		src: LANDING_IMAGES.startups.leap,
		width: 657,
		height: 192,
		alt: 'leap',
	},
];

export const TRUSTED_STARTUPS_COUNT = 600;

export const LANDING_TWEETS = [
	{
		id: 'cbi',
		name: 'ChessBase India',
		handle: 'ChessbaseIndia',
		href: 'https://x.com/ChessbaseIndia/status/2060346629811835173',
		avatar: '/images/landing/tweets/cbi.jpg',
		verified: true,
		body: 'HelpChess Foundation, the NGO initiative by ChessBase India, is looking for Software Engineering Volunteers to help build technology that supports chess players across India!\n\nIf you want to help build the future of Indian Chess, apply here: binary.so/jiQUAlD',
		postedAt: '6:34 PM · May 29, 2026',
		likes: 7,
	},
	{
		id: 'tanmay',
		name: 'Tanmay Bhat',
		handle: 'thetanmay',
		href: 'https://x.com/thetanmay/status/2033144906332660185',
		avatar: '/images/landing/tweets/tanmay.jpg',
		verified: true,
		body: 'New marketing roles are open for a bunch of new exciting personal projects. Currently looking for folks who can ideate, strategize and write. Apply here: binary.so/CYrHOPz',
		postedAt: '5:04 PM · Mar 15, 2026',
		likes: 532,
	},
	{
		id: 'varun',
		name: 'Varun Mayya',
		handle: 'waiting4_asi',
		href: 'https://x.com/waiting4_asi/status/2081708843445416140',
		avatar: '/images/landing/tweets/varun.jpg',
		verified: true,
		body: 'We have 100+ roles we’re hiring for at the moment. Writers, editors, presenters, production staff, channel managers, event staff, Unreal Engine animators, and cinematics artists. Most roles are full time, Bangalore.\n\nWe’d love to work with you.\n\nbinary.so/aeos-hiring',
		postedAt: '5:20 PM · Jul 27, 2026',
		likes: 498,
	},
	{
		id: 'dilip',
		name: 'Dilip Kumar',
		handle: 'kmr_dilip',
		href: 'https://x.com/kmr_dilip/status/2105545891717460447',
		avatar: '/images/landing/tweets/dilip.jpg',
		verified: true,
		body: "We're hiring a finance lead at Peakst8, a Zerodha backed company.\n\nWe run Hyrox and Ironman in India, plus a bunch of other formats and are building a new type of sports and fitness business in India. You'll work directly with me & the management team.\nbinary.so/Gt7qDdo",
		postedAt: '12:00 PM · Oct 1, 2026',
		likes: 357,
	},
	{
		id: 'ankur',
		name: 'Ankur Warikoo',
		handle: 'warikoo',
		href: 'https://x.com/warikoo/status/2026659896784191705',
		avatar: '/images/landing/tweets/ankur.jpg',
		verified: true,
		body: 'Hiring for Visual Communication and Design at @wariCrew\n\nFully remote\nPart-time\nCompensation range is shared in the form\n\nLast date to apply: 1st March 2026 (Sunday)\nbinary.so/ikwvNwc',
		postedAt: '7:35 PM · Feb 25, 2026',
		likes: 91,
	},
	{
		id: 'saaquib',
		name: 'Saaquib Dawoodani',
		handle: 'saaquibx',
		href: 'https://x.com/saaquibx/status/2040044049001009352',
		avatar: '/images/landing/tweets/saaquib.jpg',
		verified: true,
		body: 'New roles @BlumeVentures portcos, across US & India 🌸\n\nbinary.so/qWNJvtL\n\nGo go goooo! 🏃🏼',
		postedAt: '5:59 PM · Apr 3, 2026',
		likes: 174,
	},
] as const;

export type LandingTweet = (typeof LANDING_TWEETS)[number];
